import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useFormContext } from "../contexts/FormContext";
import MarkdownHint from "./MarkdownHint"; // ← NOUVEAU IMPORT

const ExperienceForm: React.FC = () => {
  const { formData, updateFormData } = useFormContext();
  const [experiences, setExperiences] = useState(formData.experiences.length > 0 ? formData.experiences.map(exp => ({
    nom: exp.company_name,
    debut: exp.start_date,
    fin: exp.end_date || "",
    fonction: exp.job_title,
    missionsRealisation: exp.tasks_achievements,
    environnement: exp.tools.join(', '),
    showAbstractDates: exp.show_in_abstract_dates ?? false, 
  })) : [
    {
      nom: "",
      debut: "",
      fin: "",
      fonction: "",
      missionsRealisation: "",
      environnement: "",
      showAbstractDates: false,
    },
  ]);

  // Update form context when experiences change
  useEffect(() => {
    const formattedExperiences = experiences.map(exp => ({
      company_name: exp.nom,
      start_date: exp.debut,
      end_date: exp.fin || undefined,
      job_title: exp.fonction,
      tasks_achievements: exp.missionsRealisation,
      tools: exp.environnement.split(',').map(item => item.trim()).filter(Boolean),
      client_name: "",
      billable: true,
      show_in_abstract_dates: exp.showAbstractDates,
    }));
    updateFormData('experiences', formattedExperiences);
  }, [experiences]);

  const handleExperiencesReorder = (result: any) => {
    if (!result.destination) return;
    const items = Array.from(experiences);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setExperiences(items);
  };

  return (
    <div>
      <div style={{ marginBottom: "1.2rem" }}>
        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500 }}>
          Expériences professionnelles:
          <span style={{ display: "block", fontSize: "0.9em", color: "#888", marginTop: "0.3rem" }}>
            Glisser-déposer pour réordonner les expériences
          </span>
        </label>
        <DragDropContext onDragEnd={handleExperiencesReorder}>
          <Droppable droppableId="experiences">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {experiences.map((exp, idx) => (
                  <Draggable key={`exp-${idx}`} draggableId={`exp-${idx}`} index={idx}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          border: "1px solid #e3e6ee",
                          borderRadius: "12px",
                          padding: "2rem 2rem",
                          marginBottom: "1.2rem",
                          background: "#f9fafc"
                        }}
                      >
                        <div {...provided.dragHandleProps} style={{ cursor: "grab", marginBottom: "1rem" }}>
                          <span style={{ fontSize: "1.2rem" }}>💼</span>
                        </div>
                        <div style={{ display: "flex", gap: "1.5rem", marginBottom: "1rem" }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontWeight: 500 }}>Nom de l'expérience:</label>
                            <input type="text" placeholder="Nom" value={exp.nom} onChange={e => {
                              const newExps = [...experiences];
                              newExps[idx].nom = e.target.value;
                              setExperiences(newExps);
                            }} style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #ccc" }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontWeight: 500 }}>Fonction:</label>
                            <input type="text" placeholder="Fonction" value={exp.fonction} onChange={e => {
                              const newExps = [...experiences];
                              newExps[idx].fonction = e.target.value;
                              setExperiences(newExps);
                            }} style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #ccc" }} />
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "1.5rem", marginBottom: "1rem" }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontWeight: 500 }}>Date début:</label>
                            <input type="date" value={exp.debut} onChange={e => {
                              const newExps = [...experiences];
                              newExps[idx].debut = e.target.value;
                              setExperiences(newExps);
                            }} style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #ccc" }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontWeight: 500 }}>Date fin:</label>
                            <input type="date" value={exp.fin} onChange={e => {
                              const newExps = [...experiences];
                              newExps[idx].fin = e.target.value;
                              setExperiences(newExps);
                            }} style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #ccc" }} />
                          </div>
                        </div>

                        {/* Toggle format de dates */}
                        <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
                          <label style={{ fontWeight: 500, margin: 0 }}>Format des dates :</label>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              type="button"
                              onClick={() => {
                                const newExps = [...experiences];
                                newExps[idx].showAbstractDates = false;
                                setExperiences(newExps);
                              }}
                              style={{
                                padding: "0.35rem 0.75rem",
                                borderRadius: "20px",
                                border: "2px solid",
                                borderColor: !exp.showAbstractDates ? "#1E3C73" : "#d1d5db",
                                background: !exp.showAbstractDates ? "#1E3C73" : "white",
                                color: !exp.showAbstractDates ? "white" : "#6b7280",
                                cursor: "pointer",
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                transition: "all 0.2s"
                              }}
                            >
                              📅 Mois/Année
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newExps = [...experiences];
                                newExps[idx].showAbstractDates = true;
                                setExperiences(newExps);
                              }}
                              style={{
                                padding: "0.35rem 0.75rem",
                                borderRadius: "20px",
                                border: "2px solid",
                                borderColor: exp.showAbstractDates ? "#FF9100" : "#d1d5db",
                                background: exp.showAbstractDates ? "#FF9100" : "white",
                                color: exp.showAbstractDates ? "white" : "#6b7280",
                                cursor: "pointer",
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                transition: "all 0.2s"
                              }}
                            >
                              ⏱ Durée abstraite
                            </button>
                          </div>
                          <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                            {exp.showAbstractDates
                              ? "ex: 1 an 6 mois"
                              : "ex: 01/2023 → 06/2024"}
                          </span>
                        </div>
                        
                        {/* MISSIONS & RÉALISATIONS - AVEC MARKDOWN */}
                        <div style={{ marginBottom: "1rem" }}>
                          <label style={{ fontWeight: 500, display: "block", marginBottom: "0.5rem" }}>
                            Missions & réalisations:
                          </label>
                          
                          {/* ← NOUVEAU: Indicateur markdown */}
                          <MarkdownHint />
                          
                          <textarea 
                            placeholder={`Exemple:

BUILD
- Migration de **150+ applications**
- Mise en place de *CI/CD* avec GitLab
- Containerisation avec **Docker** et Kubernetes

RUN
- Support technique niveau 3
- Monitoring et optimisation des performances`}
                            value={exp.missionsRealisation} 
                            onChange={e => {
                              const newExps = [...experiences];
                              newExps[idx].missionsRealisation = e.target.value;
                              setExperiences(newExps);
                            }}
                            rows={8}
                            style={{
                              width: "100%",
                              maxWidth: "100%",
                              padding: "0.7rem",
                              borderRadius: "6px",
                              border: "1px solid #ccc",
                              fontSize: "1rem",
                              fontFamily: "inherit",
                              resize: "vertical",
                              boxSizing: "border-box",
                              overflow: "auto",
                              whiteSpace: "pre-wrap",
                              lineHeight: "1.5"
                            }}
                          />
                          
                          {/* ← NOUVEAU: Note explicative BUILD/RUN */}
                          <div style={{
                            marginTop: "0.5rem",
                            padding: "0.75rem",
                            backgroundColor: "#fef3c7",
                            borderRadius: "8px",
                            border: "1px solid #fbbf24",
                            fontSize: "0.875rem"
                          }}>
                            <strong>💡 Astuce:</strong> Utilisez <code>BUILD</code> et <code>RUN</code> 
                            pour séparer vos missions en catégories (ces mots seront affichés en gras)
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: "1rem" }}>
                          <label style={{ fontWeight: 500 }}>Environnement:</label>
                          <input type="text" placeholder="Environnement" value={exp.environnement} onChange={e => {
                            const newExps = [...experiences];
                            newExps[idx].environnement = e.target.value;
                            setExperiences(newExps);
                          }} style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #ccc" }} />
                        </div>
                        {experiences.length > 1 && (
                          <div style={{ textAlign: "right" }}>
                            <button type="button" onClick={() => setExperiences(experiences.filter((_, i) => i !== idx))} style={{ border: "none", background: "none", color: "red", cursor: "pointer", fontSize: "1.2rem" }}>✕</button>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div style={{ textAlign: "right" }}>
          <button type="button" onClick={() => setExperiences([...experiences, {
            nom: "",
            debut: "",
            fin: "",
            fonction: "",
            missionsRealisation: "",
            environnement: "",
            showAbstractDates: false,
          }])} style={{ padding: "0.3rem 0.7rem", borderRadius: "8px", border: "1px solid #ccc", background: "#eee", cursor: "pointer", fontSize: "1rem" }}>
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExperienceForm;